import { getAccessToken } from './getAccessToken';
import moment from 'moment';

// const environmentUrl = "https://org487f0635.crm8.dynamics.com";dev
const environmentUrl = "https://orgea61b289.crm8.dynamics.com";

// Payload model: category-prefixed checklist fields (e.g. cr3ea_oprp1_fecentrepass1)
export type OPRPAndCCPData = {
  cr3ea_batchno: string | null;
  cr3ea_cycle: string;
  cr3ea_executivename: string | null;
  cr3ea_location: string | null;
  cr3ea_observedby: string | null;
  cr3ea_platform: string;
  cr3ea_productname: string;
  cr3ea_qualitytourid: string;
  cr3ea_shift: string | null;
  cr3ea_title: string;
  cr3ea_tourstartdate: string;
  [key: string]: string | null | undefined; // e.g. cr3ea_oprp1_fecentrepass1, cr3ea_oprp1_mdsensitivity
};

// Map UI category tab to API field prefix and title label
function categoryToPrefixAndLabel(category: string | null): { prefix: string; label: string } {
  if (!category) return { prefix: 'oprp1', label: 'OPRP1' };
  const m: Record<string, { prefix: string; label: string }> = {
    'OPRP 1': { prefix: 'oprp1', label: 'OPRP1' },
    'OPRP 2': { prefix: 'oprp2', label: 'OPRP2' },
    'OPRP 3': { prefix: 'oprp3', label: 'OPRP3' },
    'OPRP 4': { prefix: 'oprp4', label: 'OPRP4' },
    'CCP': { prefix: 'ccp', label: 'CCP' },
  };
  return m[category] ?? { prefix: 'oprp1', label: 'OPRP1' };
}

// Default: all 5 tabs in order — one API call (POST) per tab
const CATEGORY_TABS_ORDER: string[] = ['OPRP 1', 'OPRP 2', 'OPRP 3', 'OPRP 4', 'CCP'];
/** Quality Indore: OPRP 3 & 4 tabs removed */
export const CATEGORY_TABS_ORDER_QUALITY_INDORE: string[] = ['OPRP 1', 'OPRP 2', 'CCP'];

interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Start session handler
export async function startSessionHandler(
  cycleNum: number,
  product: string,
  executiveName: string,
  lineNo: string,
  locationFrequency: string,
  category: string
) {
  try {
    console.log('=== START SESSION HANDLER ===');
    console.log('Parameters:', { cycleNum, product, executiveName, lineNo, locationFrequency, category });

    // Validations removed - allow empty values

    // Store both generic keys and CRM-specific keys for flexibility
    // Handle empty/null values gracefully
    const startData = {
      product: (product || '').trim(),
      executiveName: (executiveName || '').trim(),
      lineNo: (lineNo || '').trim(),
      locationFrequency: (locationFrequency || '').trim(),
      category: category || 'OPRP Old Plant',
      cr3ea_productname: (product || '').trim(),
      cr3ea_executivename: (executiveName || '').trim(),
      cr3ea_lineno: (lineNo || '').trim(),
      cr3ea_location: (locationFrequency || '').trim(),
      cr3ea_category: category || 'OPRP Old Plant'
    };

    console.log('Start data to store:', startData);

    // Store in localStorage
    const storageKey = `oprp-ccp-cycle-${cycleNum}-start-data`;
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(startData));
      console.log('Data stored successfully in localStorage with key:', storageKey);
    } catch (storageError) {
      console.error('Error storing data in localStorage:', storageError);
      throw new Error('Failed to store session data in localStorage');
    }
    
    console.log('=== START SESSION HANDLER COMPLETED ===');
    
    return startData;
  } catch (error) {
    console.error('Error in startSessionHandler:', error);
    throw error;
  }
}

// Category prefixes used in API (category-prefixed checklist fields)
const CATEGORY_PREFIXES = ['oprp1', 'oprp2', 'oprp3', 'oprp4', 'ccp'] as const;
const PREFIX_TO_CATEGORY: Record<string, string> = {
  oprp1: 'OPRP 1', oprp2: 'OPRP 2', oprp3: 'OPRP 3', oprp4: 'OPRP 4', ccp: 'CCP',
};
/** Core fields present on all environments */
const CHECKLIST_KEYS_CORE = ['fecentrepass1', 'fecentrepass2', 'nfecentrepass1', 'nfecentrepass2', 'sscentrepass1', 'sscentrepass2', 'mdsensitivity'] as const;
const CHECKLIST_KEYS = [
  'fecentrepass1', 'fecentrepass2', 'fecentrepass3',
  'nfecentrepass1', 'nfecentrepass2', 'nfecentrepass3',
  'sscentrepass1', 'sscentrepass2', 'sscentrepass3',
  'mdsensitivity',
] as const;

// Legacy (unprefixed) checklist field names for old records in table
const LEGACY_CHECKLIST_KEYS = ['fecentrepass1', 'fecentrepass2', 'nfecentrepass1', 'nfecentrepass2', 'sscentrepass1', 'sscentrepass2', 'mdsensitivity'] as const;

function parseRecordToCycleData(record: Record<string, unknown>): OPRPAndCCPCycleData | null {
  const cycle = record.cr3ea_cycle as string | undefined;
  if (!cycle) return null;
  const cycleNum = String(cycle).replace('Cycle-', '');
  let category: string | null = null;
  const statuses: Record<string, string> = {};

  // 1) Prefixed format (cr3ea_oprp1_fecentrepass1, etc.) – key present even if value is null/empty
  for (const p of CATEGORY_PREFIXES) {
    const key = `cr3ea_${p}_fecentrepass1`;
    if (record[key] !== undefined) {
      category = PREFIX_TO_CATEGORY[p] ?? p;
      for (const k of CHECKLIST_KEYS) {
        const f = `cr3ea_${p}_${k}`;
        const v = record[f];
        statuses[k] = (v != null && String(v).trim() !== '') ? String(v) : 'N/A';
      }
      break;
    }
  }

  // 2) Legacy format (cr3ea_fecentrepass1, etc.) – existing records without category prefix
  if (!category) {
    const legacyKey = 'cr3ea_fecentrepass1';
    if (record[legacyKey] !== undefined) {
      category = 'OPRP 1';
      for (const k of LEGACY_CHECKLIST_KEYS) {
        const f = `cr3ea_${k}`;
        const v = record[f];
        statuses[k] = (v != null && String(v).trim() !== '') ? String(v) : 'N/A';
      }
    }
  }

  // 3) Fallback: record has cr3ea_cycle but no checklist fields – still show the cycle (e.g. old/incomplete row)
  if (!category) {
    category = 'OPRP 1';
    for (const k of LEGACY_CHECKLIST_KEYS) {
      statuses[k] = 'N/A';
    }
  }

  return {
    cycleNum,
    product: (record.cr3ea_productname as string) ?? '',
    executiveName: (record.cr3ea_executivename as string) ?? '',
    lineNo: (record.cr3ea_batchno as string) ?? (record.cr3ea_lineno as string) ?? null,
    location: (record.cr3ea_location as string) ?? null,
    category,
    platform: (record.cr3ea_platform as string) ?? null,
    fecentrepass1: statuses.fecentrepass1 ?? 'N/A',
    fecentrepass2: statuses.fecentrepass2 ?? 'N/A',
    fecentrepass3: statuses.fecentrepass3 ?? 'N/A',
    nfecentrepass1: statuses.nfecentrepass1 ?? 'N/A',
    nfecentrepass2: statuses.nfecentrepass2 ?? 'N/A',
    nfecentrepass3: statuses.nfecentrepass3 ?? 'N/A',
    sscentrepass1: statuses.sscentrepass1 ?? 'N/A',
    sscentrepass2: statuses.sscentrepass2 ?? 'N/A',
    sscentrepass3: statuses.sscentrepass3 ?? 'N/A',
    md: statuses.mdsensitivity ?? 'N/A',
  };
}

// Fetch existing cycles for OPRP/CCP (API returns category-prefixed checklist fields)
export async function fetchCycleData(QualityTourId: string) {
  try {
    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`
    };

    const baseSelect = 'createdon,cr3ea_cycle,cr3ea_productname,cr3ea_batchno,cr3ea_location,cr3ea_executivename';
    const buildSelect = (keys: readonly string[]) =>
      `${baseSelect},${CATEGORY_PREFIXES.flatMap(p => keys.map(k => `cr3ea_${p}_${k}`)).join(',')}`;

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_oprpandcpps";
    const buildUrl = (select: string) =>
      `${environmentUrl}/api/data/v${apiVersion}/${tableName}?$filter=cr3ea_qualitytourid eq '${QualityTourId}'&$select=${select}&$orderby=createdon asc`;

    let select = buildSelect([...CHECKLIST_KEYS]);
    let apiUrl = buildUrl(select);

    let response = await fetch(apiUrl, { headers });
    if (response.status === 401) {
      const retryToken = await getAccessToken();
      const RetryAccessToken = (retryToken?.token || '').toString().trim();
      const retryHeaders = {
        "Accept": "application/json",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
        "Authorization": `Bearer ${RetryAccessToken}`
      } as const;
      response = await fetch(apiUrl, { headers: retryHeaders });
    }
    if (!response.ok) {
      const errText = await response.text();
      const tryFallbackSelect =
        response.status === 400 && errText.length < 4000 && /fecentrepass3|nfecentrepass3|sscentrepass3|property/i.test(errText);
      if (tryFallbackSelect) {
        select = buildSelect([...CHECKLIST_KEYS_CORE]);
        apiUrl = buildUrl(select);
        response = await fetch(apiUrl, { headers });
        if (response.status === 401) {
          const retryToken = await getAccessToken();
          const RetryAccessToken = (retryToken?.token || '').toString().trim();
          response = await fetch(apiUrl, {
            headers: {
              Accept: 'application/json',
              'OData-MaxVersion': '4.0',
              'OData-Version': '4.0',
              Authorization: `Bearer ${RetryAccessToken}`,
            },
          });
        }
      }
      if (!response.ok) {
        const body = tryFallbackSelect ? await response.text() : errText;
        throw new Error(`Failed to fetch data: ${response.status} - ${body}`);
      }
    }

    const data = await response.json();
    const rawRecords = data.value || [];
    console.log('[OPRPAndCCP] fetchCycleData: raw records from API', rawRecords.length, 'for qualityTourId', QualityTourId);
    const cycles: OPRPAndCCPCycleData[] = [];
    rawRecords.forEach((record: Record<string, unknown>, index: number) => {
      const parsed = parseRecordToCycleData(record);
      if (parsed) {
        cycles.push(parsed);
      } else {
        console.warn('[OPRPAndCCP] fetchCycleData: record at index', index, 'could not be parsed', record);
      }
    });
    return cycles;
  } catch (error) {
    console.error('Error fetching OPRP/CCP cycle data:', error);
    return [];
  }
}

// Derive checklist statuses from items array (one tab)
function getStatusesFromChecklistItems(items: any[]): Record<string, string> {
  const pick = (group: string, labelKey: string) => items.find((i: any) => i.group === group && i.label === labelKey);
  const valFrom = (it: any) => it?.status === 'not-okay' ? `Not Okay (${it.remarks || 'No remarks'})` : (it?.status === 'okay' ? 'OK' : 'N/A');
  return {
    fecentrepass1: valFrom(pick('FE', 'Centre 1st Pass')),
    fecentrepass2: valFrom(pick('FE', 'Centre 2nd Pass')),
    fecentrepass3: valFrom(pick('FE', 'Centre 3rd Pass')),
    nfecentrepass1: valFrom(pick('NFE', 'Centre 1st Pass')),
    nfecentrepass2: valFrom(pick('NFE', 'Centre 2nd Pass')),
    nfecentrepass3: valFrom(pick('NFE', 'Centre 3rd Pass')),
    sscentrepass1: valFrom(pick('SS', 'Centre 1st Pass')),
    sscentrepass2: valFrom(pick('SS', 'Centre 2nd Pass')),
    sscentrepass3: valFrom(pick('SS', 'Centre 3rd Pass')),
    md: valFrom(items.find((i: any) => i.id === 'md-sensitivity' || i.group?.toString().startsWith('M.'))),
  };
}

// Collect estimation data for save: 5 records (one per tab), 5 API calls. Payload per tab: cr3ea_oprp1_*, cr3ea_oprp2_*, etc.
export async function collectEstimationDataCycleSave(
  cycleNum: number,
  formData: any,
  QualityTourId: string,
  UserName: string,
  selectedShift?: string | null
) {
  const savedData: OPRPAndCCPData[] = [];

  const startDataStr = localStorage.getItem(`oprp-ccp-cycle-${cycleNum}-start-data`);
  const startData = startDataStr ? JSON.parse(startDataStr) : {};
  const product = startData.cr3ea_productname || startData.product || "N/A";
  const executiveName = startData.cr3ea_executivename || startData.executiveName || null;
  const location = startData.cr3ea_location || startData.locationFrequency || null;
  const batchNo = startData.batchNo ?? startData.lineNo ?? startData.cr3ea_lineno ?? null;
  const tourDate = moment().format('MM-DD-YYYY');
  const shiftVal = selectedShift ?? sessionStorage.getItem("shiftValue") ?? null;

  const checklistByCategory = formData?.checklistItemsByCategory ?? {};
  const basePayload = {
    cr3ea_batchno: batchNo ? String(batchNo).trim() : null,
    cr3ea_cycle: `Cycle-${cycleNum}`,
    cr3ea_executivename: UserName || executiveName || null,
    cr3ea_location: location,
    cr3ea_observedby: UserName || null,
    cr3ea_platform: "Web",
    cr3ea_productname: product,
    cr3ea_qualitytourid: QualityTourId,
    cr3ea_shift: shiftVal,
    cr3ea_tourstartdate: tourDate,
  };

  const tabOrder: string[] =
    Array.isArray(formData?.categoryTabsOrder) && formData.categoryTabsOrder.length > 0
      ? formData.categoryTabsOrder
      : CATEGORY_TABS_ORDER;
  const isQualityIndore = formData?.isQualityIndore === true;

  // One record per tab (5 default, 3 for Quality Indore)
  for (const categoryLabel of tabOrder) {
    const { prefix, label } = categoryToPrefixAndLabel(categoryLabel);
    const items = Array.isArray(checklistByCategory[categoryLabel]) ? checklistByCategory[categoryLabel] : [];
    const statuses = getStatusesFromChecklistItems(items);
    const title = `OPRP_${label}_Cycle${cycleNum}_Row1_${tourDate}`;
    const data: OPRPAndCCPData = {
      ...basePayload,
      cr3ea_title: title,
      [`cr3ea_${prefix}_fecentrepass1`]: statuses.fecentrepass1,
      [`cr3ea_${prefix}_fecentrepass2`]: statuses.fecentrepass2,
      [`cr3ea_${prefix}_nfecentrepass1`]: statuses.nfecentrepass1,
      [`cr3ea_${prefix}_nfecentrepass2`]: statuses.nfecentrepass2,
      [`cr3ea_${prefix}_sscentrepass1`]: statuses.sscentrepass1,
      [`cr3ea_${prefix}_sscentrepass2`]: statuses.sscentrepass2,
      [`cr3ea_${prefix}_mdsensitivity`]: statuses.md,
    };
    if (isQualityIndore) {
      data[`cr3ea_${prefix}_fecentrepass3`] = statuses.fecentrepass3;
      data[`cr3ea_${prefix}_nfecentrepass3`] = statuses.nfecentrepass3;
      data[`cr3ea_${prefix}_sscentrepass3`] = statuses.sscentrepass3;
    }
  savedData.push(data);
  }

  return { savedData };
}

// Save section API
export async function saveSectionApiCall(data: OPRPAndCCPData[]): Promise<SaveResponse> {
  try {
    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_oprpandcpps";
    // const tableName = "cr3ea_oprpandcpps";
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results = [];
    for (const record of data) {
      let response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(record)
      });

      if (response.status === 401) {
        // Retry with a fresh token once
        const retryToken = await getAccessToken();
        const RetryAccessToken = (retryToken?.token || '').toString().trim();
        const retryHeaders = {
          "Accept": "application/json",
          "Content-Type": "application/json; charset=utf-8",
          "OData-MaxVersion": "4.0",
          "OData-Version": "4.0",
          "Prefer": "return=representation",
          "Authorization": `Bearer ${RetryAccessToken}`
        };
        response = await fetch(apiUrl, { method: 'POST', headers: retryHeaders, body: JSON.stringify(record) });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      results.push(result);
    }

    return {
      success: true,
      message: `${data.length} records saved successfully`,
      data: results
    };
  } catch (error) {
    console.error('Error saving OPRP/CCP records:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to save records. Please try again.',
      data: []
    };
  }
}

// Interface for the cycle data structure mirrored for UI (matches slice shape for Redux state)
export interface OPRPAndCCPCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNo?: string | null;
  location?: string | null;
  category?: string | null;
  /** Where the cycle was completed: "Web" or "PWA" – both are shown in PWA */
  platform?: string | null;
  fecentrepass1?: string | null;
  fecentrepass2?: string | null;
  /** Quality Indore — third centre (when CRM columns exist) */
  fecentrepass3?: string | null;
  nfecentrepass1?: string | null;
  nfecentrepass2?: string | null;
  nfecentrepass3?: string | null;
  sscentrepass1?: string | null;
  sscentrepass2?: string | null;
  sscentrepass3?: string | null;
  md?: string | null;
  // Offline data fields (for compatibility with Redux offline data structure)
  sku?: string | null;
  proof?: string | null;
  remarks?: string | null;
}

// Interface for offline saved data (matches Redux structure)
interface OfflineSavedData {
  cycleNo: number;
  records: OPRPAndCCPCycleData[];
  timestamp: number;
}

// Sync offline data to backend
export async function syncOfflineDataToBackend(
  offlineData: OfflineSavedData[],
  QualityTourId: string,
  UserName: string,
  selectedShift?: string | null
): Promise<SaveResponse> {
  try {
    console.log('=== SYNCING OFFLINE DATA TO BACKEND ===');
    console.log('Offline data to sync:', offlineData);
    console.log('Offline data length:', offlineData.length);
    console.log('Quality Tour ID:', QualityTourId);
    console.log('User Name:', UserName);
    console.log('Selected Shift:', selectedShift);

    if (!offlineData || offlineData.length === 0) {
      console.log('No offline data to sync - returning early');
      return {
        success: true,
        message: 'No offline data to sync',
        data: []
      };
    }

    const allSavedData: OPRPAndCCPData[] = [];
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    // Process each offline data item
    for (const offlineItem of offlineData) {
      console.log(`Processing offline item for cycle ${offlineItem.cycleNo}`);
      
      // Process each record in the offline item
      for (const record of offlineItem.records) {
        try {
          // Convert offline record to API format
          const apiData = await convertOfflineRecordToApiFormat(
            record,
            offlineItem.cycleNo,
            QualityTourId,
            UserName,
            selectedShift
          );
          
          allSavedData.push(apiData);
          successCount++;
        } catch (error) {
          console.error(`Error converting record for cycle ${offlineItem.cycleNo}:`, error);
          errorCount++;
          errors.push(`Cycle ${offlineItem.cycleNo}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
    }

    // Save all converted data to backend
    if (allSavedData.length > 0) {
      console.log(`Saving ${allSavedData.length} records to backend...`);
      const saveResult = await saveSectionApiCall(allSavedData);
      
      if (saveResult.success) {
        console.log('All offline data synced successfully');
        return {
          success: true,
          message: `Successfully synced ${successCount} records. ${errorCount > 0 ? `${errorCount} errors occurred.` : ''}`,
          data: saveResult.data
        };
      } else {
        throw new Error(saveResult.message);
      }
    } else {
      return {
        success: false,
        message: 'No valid data to sync',
        data: []
      };
    }

  } catch (error) {
    console.error('Error syncing offline data:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to sync offline data',
      data: []
    };
  }
}

// Convert offline record to API format (category-prefixed payload, cr3ea_batchno, no cr3ea_category/cr3ea_lineno)
async function convertOfflineRecordToApiFormat(
  record: OPRPAndCCPCycleData,
  cycleNo: number,
  QualityTourId: string,
  UserName: string,
  selectedShift?: string | null
): Promise<OPRPAndCCPData> {
  console.log('=== CONVERTING OFFLINE RECORD TO API FORMAT ===');
  console.log('Input record:', record);
  console.log('Cycle number:', cycleNo);
  console.log('Quality Tour ID:', QualityTourId);
  console.log('User Name:', UserName);
  console.log('Selected Shift:', selectedShift);

  const parseList = (s?: string | null) => {
    if (!s) return [] as string[];
    const cleaned = s.replace(/^Okays:\s*/i, '').replace(/^Defects:\s*/i, '');
    return cleaned.split(';').map(x => x.trim()).filter(Boolean);
  };
  
  const okays = new Set(parseList(record.proof));
  const defectsArr = parseList(record.remarks);
  const defectsMap = new Map<string, string>();
  defectsArr.forEach((d: string) => {
    const [k, v] = d.split(':').map((x: string) => x?.trim());
    if (k) defectsMap.set(k, v || 'No remarks');
  });
  
  const statusFor = (group: string, labelKey: string) => {
    const key = `${group} - ${labelKey}`;
    if (okays.has(key)) return 'OK';
    if (defectsMap.has(key)) return `Not Okay (${defectsMap.get(key)})`;
    return 'OK';
  };

  const startDataStr = localStorage.getItem(`oprp-ccp-cycle-${cycleNo}-start-data`);
  const startData = startDataStr ? JSON.parse(startDataStr) : {};
  const category = record.category ?? startData.cr3ea_category ?? startData.category ?? 'OPRP 1';
  const { prefix, label } = categoryToPrefixAndLabel(category);
  const tourDate = moment().format('MM-DD-YYYY');
  const title = `OPRP_${label}_Cycle${cycleNo}_Row1_${tourDate}`;
  const batchNo = startData.batchNo ?? startData.lineNo ?? startData.cr3ea_lineno ?? record.lineNo ?? null;

  const apiData: OPRPAndCCPData = {
    cr3ea_batchno: batchNo ? String(batchNo).trim() : null,
    cr3ea_cycle: `Cycle-${cycleNo}`,
    cr3ea_executivename: UserName || startData.cr3ea_executivename || startData.executiveName || record.executiveName || null,
    cr3ea_location: startData.cr3ea_location || startData.locationFrequency || record.location || null,
    cr3ea_observedby: UserName || null,
    cr3ea_platform: "Web",
    cr3ea_productname: startData.cr3ea_productname || startData.product || record.product,
    cr3ea_qualitytourid: QualityTourId,
    cr3ea_shift: selectedShift ?? sessionStorage.getItem("shiftValue") ?? null,
    cr3ea_title: title,
    cr3ea_tourstartdate: tourDate,
    [`cr3ea_${prefix}_fecentrepass1`]: statusFor('FE', 'Centre 1st Pass'),
    [`cr3ea_${prefix}_fecentrepass2`]: statusFor('FE', 'Centre 2nd Pass'),
    [`cr3ea_${prefix}_nfecentrepass1`]: statusFor('NFE', 'Centre 1st Pass'),
    [`cr3ea_${prefix}_nfecentrepass2`]: statusFor('NFE', 'Centre 2nd Pass'),
    [`cr3ea_${prefix}_sscentrepass1`]: statusFor('SS', 'Centre 1st Pass'),
    [`cr3ea_${prefix}_sscentrepass2`]: statusFor('SS', 'Centre 2nd Pass'),
    [`cr3ea_${prefix}_mdsensitivity`]: statusFor('M.D. Sensitivity & Rejection in Time', 'M.D. Sensitivity & Rejection in Time'),
  };

  const offlineIndore = (record as OPRPAndCCPCycleData & { isQualityIndore?: boolean }).isQualityIndore === true;
  if (offlineIndore) {
    const r = record as OPRPAndCCPCycleData;
    const pickPass3 = (v: string | null | undefined, g: 'FE' | 'NFE' | 'SS') =>
      v != null && String(v).trim() !== '' && v !== 'N/A' ? String(v) : statusFor(g, 'Centre 3rd Pass');
    Object.assign(apiData, {
      [`cr3ea_${prefix}_fecentrepass3`]: pickPass3(r.fecentrepass3 ?? null, 'FE'),
      [`cr3ea_${prefix}_nfecentrepass3`]: pickPass3(r.nfecentrepass3 ?? null, 'NFE'),
      [`cr3ea_${prefix}_sscentrepass3`]: pickPass3(r.sscentrepass3 ?? null, 'SS'),
    });
  }

  console.log('=== FINAL API DATA ===');
  console.log('Converted API data:', apiData);
  console.log('=== END CONVERSION ===');
  return apiData;
}


